import { RequestForQuote, RequestForQuoteStep, usePerformanceReportQuery } from '@/dealers/_gen/gql'

type Options = Parameters<typeof usePerformanceReportQuery>[0]

const applyConverted = <T extends Pick<RequestForQuote, 'step'>>(rfq: T) => ({
  ...rfq,
  converted: [
    RequestForQuoteStep.PoReceived,
    RequestForQuoteStep.Fulfilling,
    RequestForQuoteStep.Fulfilled,
  ].includes(rfq.step),
})

// Drops quotes that exist but have no creation — exactly the set of Order Now
// (buyer-submitted) quotes, which nobody quoted, so they don't belong in a
// conversion report. Every quoted request has a creation, credited to the rep
// or to Gearflow Automation.
const excludeOrderNow = <T extends { quote: { creation: unknown } | null }>(rfq: T) =>
  !rfq.quote || rfq.quote.creation !== null

const useQuery = (options: Options) => {
  const { data } = usePerformanceReportQuery(options)
  const rfqs = data?.rfqs.filter(excludeOrderNow).map(applyConverted)
  const comparisonRfqs = data?.comparisonRfqs.filter(excludeOrderNow).map(applyConverted)

  return { rfqs, comparisonRfqs }
}

type Result = ReturnType<typeof useQuery>

export type Rfq = Exclude<Result['rfqs'], undefined>[number]

export type ComparisonRfq = Exclude<Result['comparisonRfqs'], undefined>[number]

// A customer can have no organization (GEA-4987); `orgById` only ever holds the
// real ones, so `Org` itself stays non-null.
export type Org = NonNullable<Exclude<Rfq['quote'], null>['customer']['organization']>

export type Creator = NonNullable<Exclude<Rfq['quote'], null>['creation']>['creator']

export default useQuery
